import { Game } from '../game/Game';

export interface TutorialStep {
  id: string;
  message: string;
  highlight?: string;
  trigger: string;
  position: 'top' | 'bottom' | 'center';
  allowFreePlay: boolean;
}

const OVERLAY_ID = 'tutorial-overlay';
const MESSAGE_BOX_ID = 'tutorial-message-box';
const HIGHLIGHT_CLASS = 'tutorial-highlight';

export class Tutorial {
  private game: Game;
  private steps: TutorialStep[];
  private currentStep: number = 0;
  private overlay: HTMLElement | null = null;
  private messageBox: HTMLElement | null = null;
  private styleElement: HTMLStyleElement | null = null;
  active: boolean = false;

  constructor(game: Game) {
    this.game = game;
    this.steps = this.buildSteps();
  }

  private buildSteps(): TutorialStep[] {
    return [
      {
        id: 'welcome',
        message:
          'You are Dr. Alex Voss, an aspiring archaeologist. Your first dig awaits at Dusty Creek Ranch, Arizona.',
        highlight: undefined,
        trigger: 'dismiss',
        position: 'center',
        allowFreePlay: false,
      },
      {
        id: 'dig_grid',
        message:
          'Each tile can be excavated layer by layer. Click a tile to dig!',
        highlight: '#dig-canvas',
        trigger: 'dig',
        position: 'top',
        allowFreePlay: true,
      },
      {
        id: 'tool_intro',
        message:
          'The Trowel is your all-around tool. Try switching to the Brush for delicate work.',
        highlight: '#toolbar',
        trigger: 'tool_change',
        position: 'bottom',
        allowFreePlay: true,
      },
      {
        id: 'time_management',
        message:
          "When time runs out, click 'End Day' to advance to the next day.",
        highlight: '#day-bar',
        trigger: 'day_end',
        position: 'top',
        allowFreePlay: true,
      },
      {
        id: 'weather',
        message:
          'Rain slows digging. Storms stop work entirely. Plan accordingly!',
        highlight: '#weather-display',
        trigger: 'dismiss',
        position: 'top',
        allowFreePlay: false,
      },
      {
        id: 'artifact_found',
        message:
          'Artifacts start as Unknown Objects. Visit the Analysis Lab to learn more!',
        highlight: undefined,
        trigger: 'navigate_lab',
        position: 'center',
        allowFreePlay: true,
      },
      {
        id: 'analysis_lab',
        message:
          'Clean, examine, date, and document your finds. Each step reveals more information.',
        highlight: '#lab-screen',
        trigger: 'analysis',
        position: 'top',
        allowFreePlay: true,
      },
      {
        id: 'field_journal',
        message:
          'Your journal tracks all discoveries and story progress.',
        highlight: '#journal-btn',
        trigger: 'navigate_journal',
        position: 'bottom',
        allowFreePlay: true,
      },
      {
        id: 'world_map',
        message:
          'Complete sites and build reputation to unlock new, more challenging digs around the world.',
        highlight: '#map-btn',
        trigger: 'dismiss',
        position: 'bottom',
        allowFreePlay: false,
      },
      {
        id: 'ready',
        message:
          "Keep digging at Dusty Creek Ranch. The full game awaits \u2014 museums to curate, theories to submit, and history to rewrite. Good luck, Dr. Voss!",
        highlight: undefined,
        trigger: 'dismiss',
        position: 'center',
        allowFreePlay: false,
      },
    ];
  }

  start(): void {
    if (this.active) return;
    this.active = true;
    this.currentStep = 0;
    this.injectStyles();
    this.createOverlay();
    this.showMessage();
  }

  advance(): void {
    this.clearHighlight();
    this.hideMessage();

    this.currentStep += 1;

    if (this.currentStep >= this.steps.length) {
      this.complete();
      return;
    }

    // Step 5 (artifact_found, index 5) waits for the event before showing.
    // All other steps show immediately upon advancing.
    const step = this.steps[this.currentStep];
    if (step.id === 'artifact_found') {
      // This step is triggered externally via checkEvent; do not show yet.
      // Hide overlay while waiting.
      this.hideOverlay();
      return;
    }

    this.showMessage();
  }

  checkEvent(eventType: string): void {
    if (!this.active) return;
    if (this.currentStep >= this.steps.length) return;

    const step = this.steps[this.currentStep];

    // Map incoming game events to step triggers
    let matches = false;

    switch (step.trigger) {
      case 'dismiss':
        // dismiss steps are completed via button click, not external events
        break;
      case 'dig':
        matches = eventType === 'dig' || eventType === 'artifact_found';
        break;
      case 'tool_change':
        matches = eventType === 'tool_change';
        break;
      case 'day_end':
        matches = eventType === 'day_end';
        break;
      case 'navigate_lab':
        matches = eventType === 'navigate_lab' || eventType === 'screen_lab';
        break;
      case 'analysis':
        matches = eventType === 'analysis';
        break;
      case 'navigate_journal':
        matches = eventType === 'navigate_journal' || eventType === 'screen_journal';
        break;
      default:
        matches = eventType === step.trigger;
        break;
    }

    // Special case: artifact_found step is waiting to be shown
    if (step.id === 'artifact_found' && eventType === 'artifact_found') {
      this.showOverlay();
      this.showMessage();
      return;
    }

    if (matches) {
      this.advance();
    }
  }

  private showMessage(): void {
    const step = this.steps[this.currentStep];
    if (!step) return;

    this.showOverlay();

    // Build the title for each step
    const titles: Record<string, string> = {
      welcome: 'Welcome to DIG DEEP!',
      dig_grid: 'This is your dig grid',
      tool_intro: 'Great dig! Notice the tool you\u2019re using.',
      time_management: 'Each dig action costs time.',
      weather: 'Weather affects your work.',
      artifact_found: 'You found something!',
      analysis_lab: 'The Analysis Lab',
      field_journal: 'Check the Field Journal',
      world_map: 'The World Map',
      ready: "You're ready!",
    };

    const title = titles[step.id] || '';

    // Determine button text
    const buttonText = step.trigger === 'dismiss' ? 'Got it' : 'Next';
    const showNextButton = step.trigger === 'dismiss';

    // Create or update message box
    if (!this.messageBox) {
      this.messageBox = document.createElement('div');
      this.messageBox.id = MESSAGE_BOX_ID;
      document.body.appendChild(this.messageBox);
    }

    // Step counter
    const stepNum = this.currentStep + 1;
    const totalSteps = this.steps.length;

    this.messageBox.innerHTML = `
      <div class="tutorial-step-counter">Step ${stepNum} of ${totalSteps}</div>
      <h2 class="tutorial-title">${title}</h2>
      <p class="tutorial-text">${step.message}</p>
      <div class="tutorial-buttons">
        ${showNextButton ? `<button class="tutorial-btn tutorial-btn-next" id="tutorial-next-btn">${buttonText}</button>` : `<span class="tutorial-hint">Complete the action to continue...</span>`}
        <button class="tutorial-btn tutorial-btn-skip" id="tutorial-skip-btn">Skip Tutorial</button>
      </div>
    `;

    // Position the message box
    this.messageBox.className = '';
    this.messageBox.classList.add(`tutorial-pos-${step.position}`);

    // Bind button events
    const nextBtn = document.getElementById('tutorial-next-btn');
    if (nextBtn) {
      nextBtn.addEventListener('click', () => this.advance());
    }

    const skipBtn = document.getElementById('tutorial-skip-btn');
    if (skipBtn) {
      skipBtn.addEventListener('click', () => this.skip());
    }

    // Highlight element if specified
    if (step.highlight) {
      this.highlight(step.highlight);
    }
  }

  private hideMessage(): void {
    if (this.messageBox) {
      this.messageBox.remove();
      this.messageBox = null;
    }
  }

  private highlight(selector: string): void {
    this.clearHighlight();
    const el = document.querySelector(selector) as HTMLElement | null;
    if (el) {
      el.classList.add(HIGHLIGHT_CLASS);
    }
  }

  private clearHighlight(): void {
    const highlighted = document.querySelectorAll(`.${HIGHLIGHT_CLASS}`);
    highlighted.forEach((el) => el.classList.remove(HIGHLIGHT_CLASS));
  }

  complete(): void {
    this.active = false;
    this.game.tutorialComplete = true;
    this.cleanup();
  }

  skip(): void {
    this.active = false;
    this.game.tutorialComplete = true;
    this.cleanup();
  }

  isComplete(): boolean {
    return this.game.tutorialComplete;
  }

  private createOverlay(): void {
    if (this.overlay) return;
    this.overlay = document.createElement('div');
    this.overlay.id = OVERLAY_ID;
    document.body.appendChild(this.overlay);
  }

  private showOverlay(): void {
    if (!this.overlay) {
      this.createOverlay();
    }
    if (this.overlay) {
      this.overlay.style.display = 'block';
    }
  }

  private hideOverlay(): void {
    if (this.overlay) {
      this.overlay.style.display = 'none';
    }
  }

  private cleanup(): void {
    this.clearHighlight();
    this.hideMessage();
    if (this.overlay) {
      this.overlay.remove();
      this.overlay = null;
    }
    if (this.styleElement) {
      this.styleElement.remove();
      this.styleElement = null;
    }
  }

  private injectStyles(): void {
    if (this.styleElement) return;

    this.styleElement = document.createElement('style');
    this.styleElement.textContent = `
      #${OVERLAY_ID} {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.55);
        z-index: 9000;
        pointer-events: none;
      }

      #${MESSAGE_BOX_ID} {
        position: fixed;
        z-index: 9100;
        background: #E8D5B5;
        border: 2px solid #C75B39;
        border-radius: 8px;
        padding: 20px;
        max-width: 400px;
        width: 90%;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
        pointer-events: auto;
        box-sizing: border-box;
      }

      #${MESSAGE_BOX_ID}.tutorial-pos-center {
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      }

      #${MESSAGE_BOX_ID}.tutorial-pos-top {
        top: 20px;
        left: 50%;
        transform: translateX(-50%);
      }

      #${MESSAGE_BOX_ID}.tutorial-pos-bottom {
        bottom: 20px;
        left: 50%;
        transform: translateX(-50%);
      }

      .tutorial-step-counter {
        font-family: system-ui, -apple-system, sans-serif;
        font-size: 11px;
        color: #8B7355;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin-bottom: 6px;
      }

      .tutorial-title {
        font-family: Georgia, 'Times New Roman', serif;
        font-size: 20px;
        font-weight: bold;
        color: #2C1810;
        margin: 0 0 10px 0;
      }

      .tutorial-text {
        font-family: Georgia, 'Times New Roman', serif;
        font-size: 15px;
        line-height: 1.5;
        color: #3D2B1F;
        margin: 0 0 18px 0;
      }

      .tutorial-buttons {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 10px;
      }

      .tutorial-hint {
        font-family: system-ui, -apple-system, sans-serif;
        font-size: 12px;
        font-style: italic;
        color: #8B7355;
      }

      .tutorial-btn {
        font-family: system-ui, -apple-system, sans-serif;
        font-size: 13px;
        border: none;
        border-radius: 4px;
        padding: 8px 18px;
        cursor: pointer;
        transition: background 0.15s ease, transform 0.1s ease;
      }

      .tutorial-btn:active {
        transform: scale(0.97);
      }

      .tutorial-btn-next {
        background: #C75B39;
        color: #FFF;
        font-weight: 600;
      }

      .tutorial-btn-next:hover {
        background: #A8482E;
      }

      .tutorial-btn-skip {
        background: transparent;
        color: #8B7355;
        font-size: 12px;
        padding: 6px 10px;
        text-decoration: underline;
      }

      .tutorial-btn-skip:hover {
        color: #5C4D3C;
      }

      .${HIGHLIGHT_CLASS} {
        position: relative;
        z-index: 9050;
        box-shadow: 0 0 0 4px #C75B39, 0 0 15px rgba(199, 91, 57, 0.5);
        border-radius: 4px;
        pointer-events: auto;
      }
    `;

    document.head.appendChild(this.styleElement);
  }
}
